import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import PaymentModal from '../../components/parent/PaymentModal';
import {
    CalendarIcon,
    MapPinIcon,
    ClockIcon,
    TicketIcon,
    MagnifyingGlassIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const ParentEvents = () => {
    const { get } = useApi();
    const { showToast } = useAppState();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Payment state
    const [payingEvent, setPayingEvent] = useState(null);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedChildId, setSelectedChildId] = useState('');

    // Fetch parent & linked scholars
    const { data: parentData } = useQuery({
        queryKey: ['parent', 'me'],
        queryFn: () => get('/api/parents/me/'),
        staleTime: 30 * 60 * 1000,
    });

    const parentChildren = parentData?.children;
    const children = useMemo(() => {
        return parentChildren || [];
    }, [parentChildren]);

    useEffect(() => {
        if (children.length > 0 && !selectedChildId) {
            const savedId = localStorage.getItem('parent_selected_child_id');
            const found = children.find(c => String(c.id) === savedId);
            setSelectedChildId(found ? String(found.id) : String(children[0].id));
        }
    }, [children, selectedChildId]);

    const activeStudent = useMemo(() => {
        return children.find(c => String(c.id) === selectedChildId) || children[0] || null;
    }, [children, selectedChildId]);

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            const data = await get('/api/events/notices/');
            // Handle both array and paginated response
            const eventsList = Array.isArray(data) ? data : (data.results || []);
            setEvents(eventsList);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleOpenPayModal = (event) => {
        setPayingEvent(event);
        setIsPaymentModalOpen(true);
    };

    // Calculate smart initial account reference
    const initialAccountRef = useMemo(() => {
        if (!payingEvent) return '';
        const studentAdm = activeStudent?.student_id || activeStudent?.admission_number || '';
        // Extract clean uppercase keyword from event title
        const words = (payingEvent.title || 'ACTIVITY').trim().split(/\s+/);
        const cleanWord = words[0].replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        const ref = studentAdm ? `${studentAdm} ${cleanWord}` : cleanWord;
        return ref.slice(0, 12).trim();
    }, [payingEvent, activeStudent]);

    // Exact target audience badge helper
    const getTargetAudienceBadge = (event) => {
        if (event.target_type === 'all') {
            return {
                label: 'School-Wide',
                className: 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            };
        }
        if (event.target_type === 'grades') {
            const names = event.target_grade_names || [];
            const streams = event.target_stream_names || [];
            let label = 'Targeted Grades';
            if (names.length > 0) {
                label = `Grades: ${names.join(', ')}`;
            } else if (streams.length > 0) {
                label = `Streams: ${streams.join(', ')}`;
            }
            return {
                label,
                className: 'bg-blue-50 text-blue-700 border border-blue-200'
            };
        }
        if (event.target_type === 'clubs') {
            const names = event.target_club_names || [];
            const label = names.length > 0 ? names.join(', ') : 'Targeted Club';
            return {
                label,
                className: 'bg-purple-50 text-purple-700 border border-purple-200'
            };
        }
        return {
            label: 'School Event',
            className: 'bg-[#18216D]/5 text-[#18216D] border border-[#18216D]/10'
        };
    };

    // Filter events by real-time search query
    const filteredEvents = useMemo(() => {
        if (!searchQuery.trim()) return events;
        const q = searchQuery.toLowerCase();
        return events.filter(event => {
            const matchTitle = event.title?.toLowerCase().includes(q);
            const matchDesc = event.description?.toLowerCase().includes(q);
            const matchLoc = event.location?.toLowerCase().includes(q);
            const audience = getTargetAudienceBadge(event).label.toLowerCase();
            const matchAudience = audience.includes(q);
            return matchTitle || matchDesc || matchLoc || matchAudience;
        });
    }, [events, searchQuery]);

    const formatDate = (dateString, options) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return new Date(timeString).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18216D]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
                {/* Redesigned Header: Character Portfolio style with Navy Blue & Gold */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-gray-200/80">
                    <div className="space-y-2 max-w-xl">
                        <div className="flex items-center gap-2">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFC425]/10 text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-[#FFC425]/20">
                                <CalendarIcon className="w-3 h-3 text-[#FFC425]" />
                                Kianda Events & Communications
                            </div>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-black text-[#18216D] uppercase tracking-tighter">
                            Events & <span className="text-[#FFC425]">Communications</span>
                        </h1>
                        <p className="text-slate-500 font-medium italic text-xs sm:text-sm">
                            Stay informed with upcoming school activities, club sessions, and official announcements.
                        </p>
                    </div>

                    {/* Search Bar - Responsive */}
                    <div className="flex items-center w-full md:w-auto">
                        <div className="relative group w-full md:w-80">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors pointer-events-none">
                                <MagnifyingGlassIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search events, clubs, dates..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D] placeholder:text-slate-400"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 p-1"
                                >
                                    <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {filteredEvents.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5">
                        <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CalendarIcon className="w-10 h-10 text-slate-300" />
                        </div>
                        <h2 className="text-xl font-black text-[#18216D] mb-2">
                            {searchQuery ? 'No Matching Events Found' : 'No Upcoming Events'}
                        </h2>
                        <p className="text-slate-400 font-medium text-sm">
                            {searchQuery
                                ? `No events matched your search for "${searchQuery}".`
                                : 'Check back later for school announcements and activities.'}
                        </p>
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                ) : (
                    /* Desktop 2-column grid, mobile single-column */
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {filteredEvents.map((event) => {
                            const badge = getTargetAudienceBadge(event);
                            return (
                                <div
                                    key={event.id}
                                    className="group bg-white rounded-3xl border border-slate-100 p-5 sm:p-7 hover:shadow-xl hover:shadow-indigo-900/5 transition-all relative overflow-hidden flex flex-col justify-between"
                                >
                                    {event.image ? (
                                        <div className="h-44 sm:h-52 w-[calc(100%+2.5rem)] sm:w-[calc(100%+3.5rem)] -mx-5 -mt-5 sm:-mx-7 sm:-mt-7 relative mb-3 rounded-t-3xl overflow-hidden">
                                            <img
                                                src={event.image}
                                                alt={event.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                        </div>
                                    ) : (
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#18216D]/5 rounded-bl-[2.5rem] -mr-8 -mt-8 transition-transform group-hover:scale-110 pointer-events-none"></div>
                                    )}

                                    <div className="relative z-10 space-y-4 flex-1">
                                        {/* Card Header */}
                                        <div className="flex flex-col-reverse sm:flex-row sm:items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    {/* Exact Target Audience Badge */}
                                                    <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-full ${badge.className}`}>
                                                        {badge.label}
                                                    </span>
                                                    {event.start_datetime && (
                                                        <span className="text-xs font-bold text-slate-400">
                                                            {formatDate(event.start_datetime, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-1.5 leading-snug">
                                                    {event.title}
                                                </h3>
                                                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed line-clamp-3">
                                                    {event.description}
                                                </p>
                                            </div>

                                            {event.start_datetime && (
                                                <div className="self-start sm:self-auto bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100 text-center shrink-0 min-w-[80px]">
                                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
                                                        Date
                                                    </div>
                                                    <div className="text-base font-black text-[#18216D]">
                                                        {formatDate(event.start_datetime, { day: 'numeric', month: 'short' })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer Details & Pay Now */}
                                    <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 mt-4 border-t border-slate-100">
                                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-slate-400 text-xs font-bold uppercase tracking-wider">
                                            <div className="flex items-center">
                                                <ClockIcon className="w-4 h-4 mr-1.5 text-[#FFC425] shrink-0" />
                                                <span>
                                                    {event.start_datetime ? (
                                                        <>
                                                            {formatTime(event.start_datetime)}
                                                            {' - '}
                                                            {event.end_datetime ? formatTime(event.end_datetime) : 'Onwards'}
                                                        </>
                                                    ) : 'TBA'}
                                                </span>
                                            </div>

                                            {event.location && (
                                                <div className="flex items-center">
                                                    <MapPinIcon className="w-4 h-4 mr-1.5 text-rose-400 shrink-0" />
                                                    <span className="truncate max-w-[130px]">{event.location}</span>
                                                </div>
                                            )}
                                        </div>

                                        {event.has_fee && (
                                            <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                                                <div>
                                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest sm:text-right">
                                                        Entry Fee
                                                    </div>
                                                    <div className="text-base sm:text-lg font-black text-[#18216D]">
                                                        KES {parseFloat(event.cost).toLocaleString()}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenPayModal(event)}
                                                    className="px-5 py-2.5 bg-[#18216D] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-[#0D164F] active:scale-95 transition-all shadow-md shadow-indigo-900/20 flex items-center justify-center gap-1.5 shrink-0"
                                                >
                                                    <TicketIcon className="w-4 h-4" />
                                                    <span>Pay Now</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {/* Payment Modal for School Events */}
            {payingEvent && (
                <PaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => {
                        setIsPaymentModalOpen(false);
                        setPayingEvent(null);
                    }}
                    studentData={activeStudent}
                    balance={parseFloat(payingEvent.cost) || 0}
                    initialAmount={parseFloat(payingEvent.cost) || 0}
                    initialAccountRef={initialAccountRef}
                    modalTitle={`Pay for ${payingEvent.title}`}
                    subtitle={`Activity Ticket for ${activeStudent?.full_name || activeStudent?.name || activeStudent?.first_name || 'Student'}`}
                    isActivityPayment={true}
                    onPaymentSuccess={() => {
                        showToast?.(`Payment for ${payingEvent.title} initiated successfully!`, 'success');
                        fetchEvents();
                    }}
                />
            )}
        </div>
    );
};

export default ParentEvents;
